// public/v3/src/lib/pain-scales.js — CPM pain-scale catalog and helpers.
//
// NRS is the only validated scale supported here. PROMIS/BPI/ORT are later
// phases (plan P1.1/P3) and are deliberately absent from this catalog.
//
// Pure functions only — no React, no fetches. Safe to import from any page.
// Module format: CommonJS so Jest can read it directly without an esbuild step.

// severityBands: each tuple is [scoreLow, scoreHigh, severityLabel] (inclusive endpoints).
const PAIN_SCALES_BUILTIN = [
  { id: 'NRS', name: 'NRS (Numeric Rating Scale, 0-10)', maxScore: 10, severityBands: [[0,0,'no pain'],[1,3,'mild'],[4,6,'moderate'],[7,10,'severe']] },
];

function lookupPainScale(scaleId) {
  if (!scaleId) return null;
  return PAIN_SCALES_BUILTIN.find((s) => s.id === scaleId) || null;
}

function computePainSeverity(scaleId, score) {
  const scale = lookupPainScale(scaleId);
  if (!scale || typeof score !== 'number') return '';
  const band = (scale.severityBands || []).find(([lo, hi]) => score >= lo && score <= hi);
  return band ? band[2] : '';
}

// A stored assessment supports the billing gate and note ONLY when it is a
// well-formed record of a supported scale: catalog scale id, numeric score
// inside the scale's range, and a date. Malformed/imported shapes must
// neither satisfy the missing-scale gate nor be asserted as "validated".
function isValidPainAssessment(a) {
  if (!a || !a.date) return false;
  const scale = lookupPainScale(a.scale);
  if (!scale) return false;
  const score = Number(a.score);
  return a.score !== '' && a.score != null && Number.isFinite(score)
    && score >= 0 && score <= scale.maxScore;
}

function painAssessmentsForMonth(raw, monthStr) {
  const list = raw && raw.pain && Array.isArray(raw.pain.assessments)
    ? raw.pain.assessments
    : [];
  return list.filter((a) => a && String(a.date || '').startsWith(monthStr || ''));
}

// Chronologically latest (date, then ts) — NOT last inserted: the modal
// allows backdating, so a correction entered later must not shadow a newer
// assessment on the chip.
function latestPainAssessment(raw) {
  const list = raw && raw.pain && Array.isArray(raw.pain.assessments)
    ? raw.pain.assessments
    : [];
  let latest = null;
  for (const a of list) {
    if (!a) continue;
    if (!latest) { latest = a; continue; }
    const cmp = String(a.date || '').localeCompare(String(latest.date || ''));
    if (cmp > 0 || (cmp === 0 && (a.ts || 0) >= (latest.ts || 0))) latest = a;
  }
  return latest;
}

module.exports = {
  PAIN_SCALES_BUILTIN,
  lookupPainScale,
  computePainSeverity,
  isValidPainAssessment,
  painAssessmentsForMonth,
  latestPainAssessment,
};
